import type { Approval, Engagement, ScopeVersion } from '@ai-secure-twin/shared';

/**
 * Scope history as a reader needs it: what each version's standing is, whether its approval
 * still holds, and what changed between one version and the next.
 *
 * Presentation only. Nothing here authorises anything — the server decides what may be scanned
 * against the approval it records — so these exist to make a scope's history legible, not to
 * make decisions about it.
 */

export type ScopeStanding =
  'APPROVED' | 'PENDING' | 'DECLINED' | 'SUPERSEDED' | 'EXPIRED' | 'DRAFT';

export interface ScopeStatus {
  standing: ScopeStanding;
  /** The decision that applies to this version, if any. */
  decision?: Approval;
  /**
   * Whether the scope as stored still hashes to what the approver recorded.
   *
   * `unrecorded` covers approvals made before hashing existed: they cannot be checked, and
   * saying "verified" about them would be claiming something nobody measured.
   */
  integrity: 'verified' | 'changed' | 'unrecorded' | 'not-approved';
}

/** The latest decision at the SCOPE gate about this exact version. */
const decisionFor = (version: number, approvals: readonly Approval[]) =>
  [...approvals]
    .filter((a) => a.gate === 'SCOPE' && a.scopeVersion === version)
    .sort((a, b) => a.at.localeCompare(b.at))
    .at(-1);

/** True once every window has closed, so the scope can no longer authorise anything. */
const windowsClosed = (scope: ScopeVersion, now: Date) =>
  scope.windows.length > 0 && scope.windows.every((w) => new Date(w.endsAt) <= now);

export function scopeStatus(
  scope: ScopeVersion,
  engagement: Pick<Engagement, 'stage' | 'currentScopeVersion'>,
  approvals: readonly Approval[],
  now: Date = new Date(),
): ScopeStatus {
  const decision = decisionFor(scope.version, approvals);
  const integrity: ScopeStatus['integrity'] =
    decision?.decision !== 'APPROVED'
      ? 'not-approved'
      : !decision.scopeDigest || !scope.digest
        ? 'unrecorded'
        : decision.scopeDigest === scope.digest
          ? 'verified'
          : 'changed';

  const withDecision = decision ? { decision } : {};
  const latest = scope.version === engagement.currentScopeVersion;

  if (!latest) return { standing: 'SUPERSEDED', integrity, ...withDecision };
  if (decision?.decision === 'APPROVED') {
    return {
      standing: windowsClosed(scope, now) ? 'EXPIRED' : 'APPROVED',
      integrity,
      ...withDecision,
    };
  }
  if (decision?.decision === 'DECLINED')
    return { standing: 'DECLINED', integrity, ...withDecision };
  if (engagement.stage === 'SCOPE_PENDING_APPROVAL') return { standing: 'PENDING', integrity };
  return { standing: 'DRAFT', integrity };
}

export interface ScopeChange {
  field: string;
  /** What is now there and was not, and what was there and is not. */
  added: string[];
  removed: string[];
}

const windowText = (w: ScopeVersion['windows'][number]) => `${w.startsAt} → ${w.endsAt}`;

/** Each comparable aspect of a scope, flattened to lines a person can read. */
function aspects(scope: ScopeVersion): Record<string, string[]> {
  return {
    'Allowed hosts': scope.targets.allowedHosts,
    'Seed URLs': scope.targets.seedUrls,
    'Excluded paths': scope.targets.excludePaths,
    'Requests per second': [String(scope.limits.maxRequestsPerSecond)],
    'Duration (minutes)': [String(scope.limits.maxDurationMinutes)],
    'Test windows': scope.windows.map(windowText),
    'Active scanning': [scope.permits.activeScan ? 'permitted' : 'not permitted'],
    Fuzzing: [scope.permits.fuzzing ? 'permitted' : 'not permitted'],
    'Authorisation reference': [scope.authorisation.reference],
    'Authorised by': [scope.authorisation.authorisedByName],
  };
}

/**
 * What changed from `before` to `after`, aspect by aspect. An aspect that did not change is
 * left out, so a reader sees only what the approver of `after` was being asked to accept anew.
 */
export function diffScopes(before: ScopeVersion, after: ScopeVersion): ScopeChange[] {
  const a = aspects(before);
  const b = aspects(after);
  const changes: ScopeChange[] = [];

  for (const field of Object.keys(b)) {
    const was = new Set(a[field] ?? []);
    const now = new Set(b[field] ?? []);
    const added = [...now].filter((v) => !was.has(v));
    const removed = [...was].filter((v) => !now.has(v));
    if (added.length > 0 || removed.length > 0) changes.push({ field, added, removed });
  }
  return changes;
}
